import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, X, MousePointerClick, Users as UsersIcon } from "lucide-react";

const API = '/api';

const STATUS_STYLES = {
  ACTIVE: "bg-[#107C10] text-white",
  DRAFT: "bg-[#F3F2F1] text-[#5E5E5E]",
  PAUSED: "bg-[#D83B01] text-white",
  ENDED: "bg-[#E1DFDD] text-[#5E5E5E]",
};

const emptyForm = {
  name: "", status: "DRAFT", start_at: "", end_at: "",
  priority: 100, banner_url: "", href: "", alt: "",
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [clickSummary, setClickSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const fetchCampaigns = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/campaigns/`),
      axios.get(`${API}/track/campaigns/summary`, { params: { days: 30 } }).catch(() => ({ data: { summary: {} } })),
    ]).then(([c, s]) => {
      setCampaigns(c.data.campaigns);
      setClickSummary(s.data.summary || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (camp) => {
    setForm({
      name: camp.name || "",
      status: camp.status || "DRAFT",
      start_at: camp.start_at || "",
      end_at: camp.end_at || "",
      priority: camp.priority || 100,
      banner_url: camp.banner_url || "",
      href: camp.href || "",
      alt: camp.alt || "",
    });
    setEditingId(camp.id);
    setShowForm(true);
  };

  const save = async () => {
    if (editingId) {
      await axios.put(`${API}/campaigns/${editingId}`, form);
    } else {
      await axios.post(`${API}/campaigns/`, form);
    }
    setShowForm(false);
    fetchCampaigns();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/campaigns/${id}`);
    fetchCampaigns();
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="p-8 max-w-[1600px]" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Campaigns</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Banner rotation rules for email signatures</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="create-campaign-btn">
          <Plus size={14} className="mr-2" /> New Campaign
        </Button>
      </div>

      {/* Form overlay */}
      {showForm && (
        <div className="mt-6 bg-white border border-[#0F6CBD] rounded-sm p-5" data-testid="campaign-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-medium text-[#171717]">
              {editingId ? "Edit Campaign" : "New Campaign"}
            </h2>
            <button onClick={() => setShowForm(false)} className="text-[#5E5E5E] hover:text-[#171717]"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Name</label>
              <Input value={form.name} onChange={(e) => updateField("name", e.target.value)} className="border-[#E1DFDD] rounded-sm" data-testid="campaign-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Status</label>
              <select value={form.status} onChange={(e) => updateField("status", e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm" data-testid="campaign-status-select">
                <option value="DRAFT">DRAFT</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PAUSED">PAUSED</option>
                <option value="ENDED">ENDED</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Priority</label>
              <Input type="number" value={form.priority} onChange={(e) => updateField("priority", parseInt(e.target.value) || 0)} className="border-[#E1DFDD] rounded-sm" data-testid="campaign-priority-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Banner URL</label>
              <Input value={form.banner_url} onChange={(e) => updateField("banner_url", e.target.value)} placeholder="https://..." className="border-[#E1DFDD] rounded-sm" data-testid="campaign-banner-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Click-through URL</label>
              <Input value={form.href} onChange={(e) => updateField("href", e.target.value)} placeholder="https://..." className="border-[#E1DFDD] rounded-sm" data-testid="campaign-href-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Alt Text</label>
              <Input value={form.alt} onChange={(e) => updateField("alt", e.target.value)} className="border-[#E1DFDD] rounded-sm" data-testid="campaign-alt-input" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={save} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="save-campaign-btn">
              {editingId ? "Update" : "Create"} Campaign
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#E1DFDD] rounded-sm">Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#5E5E5E] mt-6">Loading campaigns...</p>
      ) : campaigns.length === 0 ? (
        <div className="mt-8 text-center py-12 border border-dashed border-[#E1DFDD] rounded-sm">
          <p className="text-sm text-[#5E5E5E]">No campaigns yet.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="campaigns-list">
          {campaigns.map((camp) => {
            const stats = clickSummary[camp.id] || { clicks: 0, unique_clickers: 0 };
            return (
            <div key={camp.id} className="bg-white border border-[#E1DFDD] rounded-sm p-5 flex items-center gap-4" data-testid={`campaign-card-${camp.id}`}>
              {camp.banner_url && (
                <img src={camp.banner_url} alt={camp.alt} className="h-12 rounded-sm border border-[#E1DFDD]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-[#171717] text-sm">{camp.name}</h3>
                  <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold ${STATUS_STYLES[camp.status] || ""}`}>
                    {camp.status}
                  </span>
                </div>
                <p className="text-xs text-[#5E5E5E] mt-0.5">Priority: {camp.priority}</p>
              </div>
              {/* Click analytics — 30 day window */}
              <div className="flex items-center gap-4 text-right pr-2" data-testid={`campaign-stats-${camp.id}`}>
                <div className="flex flex-col items-end">
                  <span className="inline-flex items-center gap-1 text-xs text-[#5E5E5E]">
                    <MousePointerClick size={11} /> Clicks (30d)
                  </span>
                  <span className="font-mono text-base font-semibold text-[#0F6CBD] tabular-nums">{stats.clicks}</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="inline-flex items-center gap-1 text-xs text-[#5E5E5E]">
                    <UsersIcon size={11} /> Unique
                  </span>
                  <span className="font-mono text-base font-semibold text-[#171717] tabular-nums">{stats.unique_clickers}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(camp)} className="p-1.5 text-[#5E5E5E] hover:text-[#0F6CBD] hover:bg-[#EBF3FC] rounded-sm transition-colors" data-testid={`edit-campaign-${camp.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(camp.id)} className="p-1.5 text-[#5E5E5E] hover:text-[#A4262C] hover:bg-red-50 rounded-sm transition-colors" data-testid={`delete-campaign-${camp.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
